import { useCustom, useGetIdentity, useGetLocale, useTranslate } from "@refinedev/core";
import { FormCard, SectionHeader, StatusBadge, type StatusTone } from "@fawkes/ui";

import { formatEuro } from "../i18n/format";

// Time-of-day greeting, reusing the main dashboard's `dashboard.greeting.*` bundle. Capitalises the
// first letter so a login-name like "admin" greets as "Admin" (parity with the Jmix `nameSuffix()`).
/** Source parity: the per-day chart is labelled with weekday abbreviations (ma/di/wo…), not full dates.
 *  A bare yyyy-MM-dd is read as UTC midnight so the weekday never shifts across the timezone boundary. */
function weekdayLabel(iso: string, locale: string): string {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(d);
}

function cremGreeting(t: (key: string) => string, name: string): string {
  const hour = new Date().getHours();
  let key = "dashboard.greeting.evening";
  if (hour < 6) key = "dashboard.greeting.night";
  else if (hour < 12) key = "dashboard.greeting.morning";
  else if (hour < 18) key = "dashboard.greeting.afternoon";
  let g = t(key);
  if (!g || g === key) g = t("dashboard.greeting");
  const display = name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
  return display ? `${g}, ${display}` : g;
}

// Crematorium (Epic 21, MR-5): the dashboard — the React port of the Jmix
// `CrematoriumDashboardView`. One GET returns all KPIs (counts, outstanding/revenue, the
// processing-form distribution this month, the recent dossiers and the 7-day completed-cremations
// series). Read-only; the Jmix view stays reachable by its @Route (strangler-fig).
//
// GET uses a RELATIVE url (the dataProvider prepends /api once). ROLE_CREMATORIUM_VIEW is
// server-enforced; a 403 surfaces inline.

interface RecentDossier {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  eigenaarNaam: string | null;
  status: string;
}
interface VerdelingItem {
  verwerkingsvorm: string;
  aantal: number;
}
interface DagTelling {
  datum: string;
  aantal: number;
}
interface Dashboard {
  nieuweAanmeldingen: number;
  ophalenVandaag: number;
  ontvangen: number;
  geplandeCrematies: number;
  asKlaarVoorRetour: number;
  wachtOpBetaling: number;
  nogNietIngepland: number;
  facturenVervallen: number;
  openstaandBedrag: number;
  omzetDezeMaand: number;
  recenteDossiers: RecentDossier[];
  verdelingDezeMaand: VerdelingItem[];
  crematiesPerDag: DagTelling[];
}

const STATUS_TONE: Record<string, StatusTone> = {
  AFGEROND: "success",
  GECREMEERD: "success",
  GEANNULEERD: "danger",
  WACHT_OP_BETALING: "warning",
};

export function CrematoriumDashboard({
  onNewAanmelding,
  onRelaties,
  onPlanning,
  onAsRetour,
  onOpenAanmeldingen,
  onOpenDossiers,
  onOpenFacturen,
}: {
  onNewAanmelding?: () => void;
  onRelaties?: () => void;
  onPlanning?: () => void;
  onAsRetour?: () => void;
  onOpenAanmeldingen?: () => void;
  onOpenDossiers?: () => void;
  onOpenFacturen?: () => void;
} = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data: identity } = useGetIdentity<{ id: string; name?: string }>();

  const { data, isLoading, isError } = useCustom<Dashboard>({
    url: "crematorium/dashboard",
    method: "get",
  });
  const d = data?.data;

  if (isLoading) {
    return (
      <FormCard>
        <p className="fawkes-muted">{t("common.loading")}</p>
      </FormCard>
    );
  }
  if (isError || !d) {
    return (
      <FormCard>
        <p className="fawkes-error">{t("crematoriumDashboard.load.error")}</p>
      </FormCard>
    );
  }

  // Source parity: each KPI is clickable and navigates to its section (crem-clickable).
  const kpis: { key: string; value: string; tone: StatusTone; nav?: () => void }[] = [
    { key: "nieuweAanmeldingen", value: String(d.nieuweAanmeldingen), tone: "neutral", nav: onOpenAanmeldingen },
    { key: "ophalenVandaag", value: String(d.ophalenVandaag), tone: "neutral", nav: onPlanning },
    { key: "ontvangen", value: String(d.ontvangen), tone: "neutral", nav: onOpenDossiers },
    { key: "geplandeCrematies", value: String(d.geplandeCrematies), tone: "neutral", nav: onPlanning },
    { key: "asKlaarVoorRetour", value: String(d.asKlaarVoorRetour), tone: "success", nav: onAsRetour },
    { key: "openstaandBedrag", value: formatEuro(d.openstaandBedrag, locale), tone: "warning", nav: onOpenFacturen },
  ];

  const acties: { key: string; value: number; tone: StatusTone }[] = [
    { key: "wachtOpBetaling", value: d.wachtOpBetaling, tone: "danger" },
    { key: "nogNietIngepland", value: d.nogNietIngepland, tone: "warning" },
    { key: "facturenVervallen", value: d.facturenVervallen, tone: "danger" },
    { key: "asKlaarVoorRetour", value: d.asKlaarVoorRetour, tone: "success" },
  ];

  const verdelingTotaal = d.verdelingDezeMaand.reduce((s, v) => s + v.aantal, 0);
  const maxDag = Math.max(1, ...d.crematiesPerDag.map((x) => x.aantal));

  const greeting = cremGreeting(t, (identity?.name ?? "").trim());

  return (
    <div className="crem-dash">
      {/* Hero: greeting + static subtitle (parity with Jmix `buildHeader()`). */}
      <div className="crem-dash__hero">
        <h1 className="crem-dash__greeting">{greeting} 👋</h1>
        <p className="crem-dash__subtitle">{t("crematoriumDashboard.subtitle")}</p>
      </div>

      {/* Snelle acties bar (parity with Jmix `buildQuickActions()`). */}
      <div className="crem-dash__actions">
        <span className="crem-dash__actions-title">{t("crematoriumDashboard.snelacties.title")}</span>
        <button type="button" className="crem-dash__action crem-dash__action--primary" onClick={onNewAanmelding}>
          {t("crematoriumDashboard.action.nieuw")}
        </button>
        <button type="button" className="crem-dash__action" onClick={onRelaties}>
          {t("crematoriumDashboard.action.relatie")}
        </button>
        <button type="button" className="crem-dash__action" onClick={onPlanning}>
          {t("crematoriumDashboard.action.ophaling")}
        </button>
        <button type="button" className="crem-dash__action" onClick={onAsRetour}>
          {t("crematoriumDashboard.action.asRetour")}
        </button>
      </div>

      {/* KPI cards */}
      <div className="crem-dash__kpis">
        {kpis.map((k) => (
          <div
            key={k.key}
            className={`crem-dash__kpi crem-dash__kpi--${k.tone}${k.nav ? " crem-dash__kpi--clickable" : ""}`}
            role={k.nav ? "button" : undefined}
            tabIndex={k.nav ? 0 : undefined}
            onClick={k.nav}
            onKeyDown={
              k.nav
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      k.nav?.();
                    }
                  }
                : undefined
            }
          >
            <span className="crem-dash__kpi-value">{k.value}</span>
            <span className="crem-dash__kpi-label">{t(`crematoriumDashboard.kpi.${k.key}`)}</span>
          </div>
        ))}
      </div>

      <div className="crem-dash__row">
        {/* Acties vereist */}
        <FormCard>
          <SectionHeader>{t("crematoriumDashboard.acties.title")}</SectionHeader>
          <ul className="crem-dash__acties">
            {acties.map((a) => (
              <li key={a.key}>
                <StatusBadge tone={a.tone}>{a.value}</StatusBadge>
                <span>{t(`crematoriumDashboard.kpi.${a.key}`)}</span>
              </li>
            ))}
          </ul>
        </FormCard>

        {/* Omzet deze maand */}
        <FormCard>
          <SectionHeader>{t("crematoriumDashboard.omzet.title")}</SectionHeader>
          <div className="crem-dash__omzet">
            <div>
              <span className="crem-dash__kpi-value">{formatEuro(d.omzetDezeMaand, locale)}</span>
              <span className="crem-dash__kpi-label">{t("crematoriumDashboard.omzet.dezeMaand")}</span>
            </div>
            <div>
              <span className="crem-dash__kpi-value">{formatEuro(d.openstaandBedrag, locale)}</span>
              <span className="crem-dash__kpi-label">{t("crematoriumDashboard.omzet.openstaand")}</span>
            </div>
          </div>
        </FormCard>
      </div>

      <div className="crem-dash__row">
        {/* Verdeling deze maand */}
        <FormCard>
          <SectionHeader>{t("crematoriumDashboard.verdeling.title")}</SectionHeader>
          {d.verdelingDezeMaand.length === 0 ? (
            <p className="fawkes-muted">{t("crematoriumDashboard.verdeling.empty")}</p>
          ) : (
            <ul className="crem-dash__verdeling">
              {d.verdelingDezeMaand.map((v) => {
                const pct = verdelingTotaal > 0 ? Math.round((v.aantal / verdelingTotaal) * 100) : 0;
                return (
                  <li key={v.verwerkingsvorm}>
                    <span className="crem-dash__verdeling-label">
                      {t(`crematoriumTarief.vorm.${v.verwerkingsvorm}`)}
                    </span>
                    <span className="crem-dash__bar">
                      <span className="crem-dash__bar-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="crem-dash__verdeling-count">
                      {v.aantal} ({pct}%)
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </FormCard>

        {/* Crematies per dag (laatste 7) */}
        <FormCard>
          <SectionHeader>{t("crematoriumDashboard.perDag.title")}</SectionHeader>
          <div className="crem-dash__chart">
            {d.crematiesPerDag.map((day) => (
              <div key={day.datum} className="crem-dash__chart-col" title={`${day.aantal}`}>
                <span
                  className="crem-dash__chart-bar"
                  style={{ height: `${Math.round((day.aantal / maxDag) * 100)}%` }}
                />
                <span className="crem-dash__chart-label">{weekdayLabel(day.datum, locale)}</span>
                <span className="crem-dash__chart-value">{day.aantal}</span>
              </div>
            ))}
          </div>
        </FormCard>
      </div>

      {/* Recente dossiers */}
      <FormCard>
        <SectionHeader>{t("crematoriumDashboard.recent.title")}</SectionHeader>
        {d.recenteDossiers.length === 0 ? (
          <p className="fawkes-muted">{t("crematoriumDashboard.recent.empty")}</p>
        ) : (
          <ul className="crem-dash__recent">
            {d.recenteDossiers.map((r) => (
              <li key={r.id}>
                <span className="crem-dash__recent-nummer">{r.dossierNummer ?? "—"}</span>
                <span className="crem-dash__recent-naam">
                  {r.dierNaam ?? "—"}
                  {r.eigenaarNaam ? ` · ${r.eigenaarNaam}` : ""}
                </span>
                <StatusBadge tone={STATUS_TONE[r.status] ?? "neutral"}>
                  {t(`crematoriumDashboard.status.${r.status}`)}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </FormCard>
    </div>
  );
}
